/**
 * Run `op` over `items` with at most `limit` in flight, and keep every outcome.
 *
 * **A deliberate sibling of `src/routes/transactions/runBulk.ts`, not a copy of
 * it.** `runBulk` reduces each item to `done` ids and `failed` reasons, and adds
 * progress and abort — what a 300-row bulk edit needs. Add all needs the
 * opposite: the VALUE each success returned (how many past payments it linked,
 * summed into the toast) and the REASON each failure carries (a 400 and a 500
 * read differently). So this resolves a `PromiseSettledResult` per item, in
 * input order, and nothing else.
 *
 * Workers each pull the next index, rather than running batches: a batch waits
 * for its slowest member, and one slow request would idle the other lanes. One
 * rejection never stops the rest — giving up at the first 500 would leave the
 * user unable to tell what landed. Unifying the two runners is a follow-up
 * outside this module.
 */
export async function pool<T, R>(
  items: readonly T[],
  limit: number,
  op: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const out: PromiseSettledResult<R>[] = Array.from({ length: items.length })
  let next = 0
  async function worker() {
    while (next < items.length) {
      const i = next++
      try {
        out[i] = { status: 'fulfilled', value: await op(items[i] as T) }
      } catch (reason) {
        out[i] = { status: 'rejected', reason }
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return out
}
